import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Save, Search } from 'lucide-react';
import VehiclesSection from './VehiclesSection';
import DriversSection from './DriversSection';

/**
 * Configure `items_per_container` per category and per product + vehicle fleet capacity.
 * Reusable panel (used as a tab inside AdminContainers and on the standalone config page).
 */
export const ContainersConfigPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultIpc, setDefaultIpc] = useState(10);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [dirtyCats, setDirtyCats] = useState({});
  const [dirtyProds, setDirtyProds] = useState({});

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/containers/config`, { headers: getAuthHeaders() });
      setDefaultIpc(res.data.default_items_per_container ?? 10);
      setCategories(res.data.categories || []);
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error('Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  };

  const categoryById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.trim().toLowerCase();
    return products.filter(p => p.name.toLowerCase().includes(q));
  }, [products, search]);

  const productsByCategory = useMemo(() => {
    const groups = {};
    for (const p of filteredProducts) {
      const key = p.category_id || '__no_cat__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    return groups;
  }, [filteredProducts]);

  const onChangeCategory = (id, raw) => {
    const v = raw === '' ? null : Math.max(1, Math.min(99999, parseInt(raw) || 1));
    setDirtyCats(prev => ({ ...prev, [id]: v }));
  };
  const onChangeProduct = (id, raw) => {
    const v = raw === '' ? null : Math.max(1, Math.min(99999, parseInt(raw) || 1));
    setDirtyProds(prev => ({ ...prev, [id]: v }));
  };

  const getEffective = (p) => {
    if (dirtyProds[p.id] !== undefined && dirtyProds[p.id] !== null) return dirtyProds[p.id];
    if (p.items_per_container !== null && p.items_per_container !== undefined && dirtyProds[p.id] === undefined) {
      return p.items_per_container;
    }
    const cat = categoryById[p.category_id];
    const catVal = dirtyCats[p.category_id] !== undefined ? dirtyCats[p.category_id] : cat?.items_per_container;
    if (catVal) return `${catVal} (из категории)`;
    return `${defaultIpc} (default)`;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        categories: Object.entries(dirtyCats).map(([id, v]) => ({ id, items_per_container: v })),
        products: Object.entries(dirtyProds).map(([id, v]) => ({ id, items_per_container: v })),
      };
      const res = await axios.post(`${API}/admin/containers/config`, payload, { headers: getAuthHeaders() });
      toast.success(`Сохранено: категорий ${res.data.categories_updated}, товаров ${res.data.products_updated}`);
      setDirtyCats({}); setDirtyProds({});
      fetchConfig();
    } catch (e) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(dirtyCats).length + Object.keys(dirtyProds).length;

  if (loading) return <div className="p-8 text-center text-muted-foreground">Загрузка...</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="py-4 text-sm space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <div><b>Что это:</b> сколько штук товара помещается в <b>один контейнер</b>. Используется в логистике для авто-расчёта загрузки машины и предложения разделения на 2-3 машины.</div>
              <div>Дефолт по умолчанию: <Badge variant="outline">{defaultIpc}</Badge> шт/контейнер.</div>
              <div>Правило: <b>товар → категория → дефолт</b>. Если у товара поле пустое — берётся значение категории. Если и у категории пусто — дефолт {defaultIpc}.</div>
            </div>
            <Button onClick={save} disabled={!dirtyCount || saving} data-testid="save-containers-config" className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Сохраняю...' : `Сохранить${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Категории</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {categories.map(c => {
            const val = dirtyCats[c.id] !== undefined ? dirtyCats[c.id] : c.items_per_container;
            return (
              <div key={c.id} className="flex items-center gap-3 py-1 border-b last:border-b-0">
                <span className="flex-1 text-sm">{c.name_ru}</span>
                <Input
                  type="number"
                  min="1"
                  max="99999"
                  placeholder={`по умолчанию ${defaultIpc}`}
                  value={val ?? ''}
                  onChange={e => onChangeCategory(c.id, e.target.value)}
                  className="w-44"
                  data-testid={`cat-ipc-${c.id}`}
                />
                <span className="text-xs text-muted-foreground w-24 text-right">шт/контейнер</span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Товары ({filteredProducts.length} из {products.length})</CardTitle>
            <div className="relative w-72">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Поиск товара..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8"
                data-testid="product-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(productsByCategory).map(([catId, prods]) => {
            const cat = categoryById[catId];
            return (
              <div key={catId}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1 pb-1 border-b">
                  {cat?.name_ru || 'Без категории'}
                </div>
                {prods.map(p => {
                  const val = dirtyProds[p.id] !== undefined ? dirtyProds[p.id] : p.items_per_container;
                  return (
                    <div key={p.id} className="flex items-center gap-3 py-1">
                      <span className="flex-1 text-sm truncate">{p.name}</span>
                      <Input
                        type="number"
                        min="1"
                        max="99999"
                        placeholder={typeof getEffective(p) === 'string' ? getEffective(p) : ''}
                        value={val ?? ''}
                        onChange={e => onChangeProduct(p.id, e.target.value)}
                        className="w-44"
                        data-testid={`prod-ipc-${p.id}`}
                      />
                      <span className="text-xs text-muted-foreground w-24 text-right truncate">{typeof getEffective(p) === 'string' ? getEffective(p) : ''}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <VehiclesSection />
      <DriversSection />
    </div>
  );
};

export default ContainersConfigPanel;
